/**
 * usePractitionerProfile — Data hook for practitioner identity in Settings.
 *
 * Fetches the current user's practitioner_profiles row via React Query.
 * Returns isPractitioner=true only when the row exists with status=approved.
 *
 * Wire shape is owned by `PractitionerProfileDto` in
 * `@dramadetector/shared/schemas`. The hook validates the server response
 * via `fetchJsonValidated` and converts the snake_case DTO into the
 * camelCase `PractitionerProfile` interface used by the UI.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import {
	canAccessStrictAuthEndpoints,
	getAuthCapability,
} from "../lib/auth-capability";
import { logNetworkError } from "../lib/logNetworkError";
import {
	fetchJsonValidated,
	DtoValidationError,
	getAuthFailureReason,
} from "@dramadetector/api-client";
import {
	PractitionerProfileDto,
	type PractitionerProfile as PractitionerProfileDtoShape,
} from "@dramadetector/shared/schemas";
import { getEdgeFunctionUrl } from "../lib/api-url";

export type PracticeType = "therapist" | "coach" | "counselor" | "other";
export type CertificationStatus = "certified" | "pending" | "suspended";
export type PractitionerStatus =
	| "approved"
	| "invited"
	| "applied"
	| "rejected"
	| "suspended";

export interface PractitionerProfile {
	id: string;
	practiceType: PracticeType;
	practiceName: string;
	certificationStatus: CertificationStatus;
	foundingPractitioner: boolean;
	status: PractitionerStatus;
	referralCode: string;
}

export interface UsePractitionerProfileReturn {
	/**
	 * True when the current user has a practitioner_profiles row with status=approved.
	 * False when no row exists or status is not approved.
	 */
	isPractitioner: boolean;
	/** Null when isPractitioner is false or isLoading is true. */
	profile: PractitionerProfile | null;
	isLoading: boolean;
}

// Query key factory
export const practitionerProfileKeys = {
	all: ["practitioner-profile"] as const,
	user: (userId: string | null) =>
		[...practitionerProfileKeys.all, "user", userId] as const,
};

/** snake_case DTO → camelCase UI shape. */
function dtoToProfile(dto: PractitionerProfileDtoShape): PractitionerProfile {
	return {
		id: dto.id,
		practiceType: dto.practice_type,
		practiceName: dto.practice_name ?? "",
		certificationStatus: dto.certification_status,
		foundingPractitioner: dto.founding_practitioner,
		status: dto.status,
		referralCode: dto.referral_code ?? "",
	};
}

export function usePractitionerProfile(): UsePractitionerProfileReturn {
	const { user, accessToken, loading: authLoading, isRealUser } = useAuth();
	const userId = user?.id ?? null;
	const authCapability = getAuthCapability({
		loading: authLoading,
		isRealUser,
	});
	const canAccessStrictAuth = canAccessStrictAuthEndpoints({
		loading: authLoading,
		isRealUser,
	});

	const { data, isLoading } = useQuery({
		queryKey: practitionerProfileKeys.user(userId),
		queryFn: async (): Promise<PractitionerProfile | null> => {
			if (!userId) return null;

			try {
				const dto = await fetchJsonValidated(
					PractitionerProfileDto,
					getEdgeFunctionUrl("get-practitioner-profile"),
				);
				return dtoToProfile(dto);
			} catch (err) {
				// Handler returns `null` for users with no practitioner_profiles
				// row — that fails the schema parse with a `DtoValidationError`.
				// Treat it as "not a practitioner" rather than surfacing the error.
				if (err instanceof DtoValidationError) {
					return null;
				}
				logNetworkError(err, {
					hook: "usePractitionerProfile",
					endpoint: "get-practitioner-profile",
					isAuthenticated: authCapability === "authenticated",
					extra: {
						authCapability,
						authFailureReason: getAuthFailureReason(err) ?? undefined,
					},
				});
				throw err;
			}
		},
		staleTime: 1000 * 60 * 5, // 5 minutes
		gcTime: 1000 * 60 * 30,
		enabled: canAccessStrictAuth && !!userId,
		refetchOnMount: true,
	});

	const profile = data ?? null;

	return {
		isPractitioner: profile?.status === "approved",
		profile,
		isLoading: isLoading && !!accessToken,
	};
}
